import storage, { ExpirableStorage } from '../utils/storage'
import { getNtpAppVersion } from '#imports'
/**
 * This plugin is used to migrate the app's local storage when the build version changes.
 * It checks the current build version against the stored version in local storage.
 * If the versions are different, it clears the cached configs from local storage and updates the stored version.
 * Everything under the `app:config` prefix is cleared, including variant-suffixed keys.
 * The current build version is obtained from the runtime configuration.
 * The plugin also provides the current build version and the storage object to the Nuxt app.
 */

/**
 * MOTIVATION:
 * 1. The app's local storage may contain data that is no longer relevant or compatible with the new build version.
 * 2. By clearing specific keys, we ensure that the app starts with a clean slate for the new build version.
 * 3. This helps prevent potential issues that may arise from using outdated or incompatible data.
 */

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const appVersion = getNtpAppVersion()
  const buildVersion = config.public.buildVersion || appVersion || 'next'
  try {
    // Cleared by prefix rather than a hardcoded list: a list silently misses a newly
    // added config store, and can never match the variant-suffixed keys
    // (`app:config:<key>@<variant>`) that a split test or flag override produces.
    const configCache = new ExpirableStorage(storage, 'app:config', 0)

    // if query param is set to nocache, drop the cached configs. Scoped to the config
    // namespace so it doesn't reset engagement/theme state, which would in turn move
    // the flags that depend on it.
    if ('nocache' in nuxtApp._route.query) {
      console.log('Clearing cached configs due to no-cache query param')
      configCache.clear()
    }

    const storedVersion = storage.getItem('app:buildVersion')
    if (buildVersion !== storedVersion) {
      configCache.clear()
      storage.setItem('app:buildVersion', `${buildVersion}`)
    }
    // eslint-disable-next-line
  } catch (error) {
    /* empty */
  }
  nuxtApp.provide('buildVersion', buildVersion)
  nuxtApp.provide('storage', storage)
})
